
'use client'

import React, { useState } from 'react';
import { Grid, Typography, Box, Paper, Button, Container, List, ListItem, ListItemText, ListItemIcon, IconButton } from '@mui/material';
import { getDictionary, getField } from '../../utils/translation/translation';
import './style.css'
import { useRouter } from 'next/navigation';
import useTranslations from '../../hooks/useTranslation';
import useIsMobile from '../../hooks/useIsMobile';
import { useNavbar } from '../../contexts/NavbarContext';
import { useTheme } from '../../components/layoutManager';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { getSvg } from '../../utils/svgUtils';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import GoogleIcon from '@mui/icons-material/Google';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BarChartIcon from '@mui/icons-material/BarChart';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LinkIcon from '@mui/icons-material/Link';
import ClearIcon from '@mui/icons-material/Clear';
export default function SeoServices() {
    const {
        loading,
        translations,
        supportedLanguages,
        language,
        selectedLanguage,
        handleLanguageChange,
    } = useTranslations();
    const isMobile = useIsMobile();
    const phoneNumber = '+971501286691';
    const isArabic = language === 'arabic';
    const dictionary = getDictionary(translations, 'seo', language);
    const { companyRef, productsRef, blogRef, contactusRef, scrollToSection } = useNavbar();
    const { mode } = useTheme();



    const cloudixFeatures = [
        getField(dictionary, 'real-time-keyword', language),
        getField(dictionary, 'predective-search', language),
        getField(dictionary, 'automated-competitor', language),
        getField(dictionary, 'smart-review', language),
    ];


    const aiTransformFeatures = [
        {
            icon: <TrackChangesIcon sx={{ color: 'var(--accent-primary)' }} />,
            title: getField(dictionary, 'intelligent-key-tergeting', language),
            description: getField(dictionary, 'our-ai-continiously', language)
        },
        {
            icon: <FlashOnIcon sx={{ color: 'var(--accent-primary)' }} />,
            title: getField(dictionary, 'automated-content', language),
            description: getField(dictionary, 'our-system-optimizes', language),
        },
        {
            icon: <ChatBubbleOutlineIcon sx={{ color: 'var(--accent-primary)' }} />,
            title: getField(dictionary, 'sentiment-analysis', language),
            description: getField(dictionary, 'customer-reviews-ai', language)
        },
        {
            icon: <AnalyticsIcon sx={{ color: 'var(--accent-primary)' }} />,
            title: getField(dictionary, 'predective-analytics', language),
            description: getField(dictionary, 'our-system-predicts', language)
        },
    ];


    const googleSearchStats = [
        { percent: `${getField(dictionary, '76', language)}%`, text: getField(dictionary, 'of-people-search', language) },
        { percent: `${getField(dictionary, '86', language)}%`, text: getField(dictionary, 'of-consumers-google', language) },
        { percent: getField(dictionary, 'top-positions', language), text: getField(dictionary, 'in-local-search', language) },
    ];

    const topSearchQueries = [
        getField(dictionary, 'best-resto', language),
        getField(dictionary, 'resto-near-me', language),
        getField(dictionary, 'resto-opens-now', language),
        getField(dictionary, 'resto-without-outdoor', language),
        getField(dictionary, 'restaurants', language),
    ];

    const googleMapsExamples = [
        { rank: 1, name: getField(dictionary, 'your-rest-name', language), reviews: '120+', type: getField(dictionary, 'italian-resto', language), stars: 5 },
        { rank: 2, name: getField(dictionary, 'competitor-resto', language), reviews: '85', type: getField(dictionary, 'italian-resto', language), stars: 4 },
        { rank: 3, name: getField(dictionary, 'competitor-resto', language), reviews: '42', type: getField(dictionary, 'italian-resto', language), stars: 3 },
    ];

    const mobileSearchStats = [
        { percent: `${getField(dictionary, '50', language)}%`, text: getField(dictionary, 'of-near-me-search', language) },
        { percent: `${getField(dictionary, '78', language)}%`, text: getField(dictionary, 'of-local-mobile', language) },
        { percent: getField(dictionary, 'google-maps-users', language), text: getField(dictionary, 'more-likely', language) },
    ];
    const howItWorksSteps = [
        {
            icon: `${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/search.png`,
            circleBg: '#8130CA54',
            title: getField(dictionary, 'local-seo-audit', language),
            description: getField(dictionary, 'we-analyze-current', language)
        },
        {
            icon: `${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/edit.png`,
            circleBg: '#FBF6C5',
            title: getField(dictionary, 'optimization-strategy', language),
            description: getField(dictionary, 'we-develop-strategy', language)
        },
        {
            icon: `${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/pie-chart.png`,
            circleBg: '#8130CA54',
            title: getField(dictionary, 'ongoing-management', language),
            description: getField(dictionary, 'we-monitor', language)
        },
    ];
    const seoFeatures = [
        {
            icon: <LocationOnIcon sx={{ fontSize: '2rem', color: 'var(--accent-primary)' }} />,
            circleBg: '#f0e6fa',
            title: getField(dictionary, 'local-search-optimization', language),
            description: getField(dictionary, 'online-presence', language)
        },
        {
            icon: <GoogleIcon sx={{ fontSize: '2rem', color: 'var(--accent-secondary)' }} />,
            circleBg: '#fffbe6',
            title: getField(dictionary, 'google-business', language),
            description: getField(dictionary, 'we-fully-optimize', language)
        },
        {
            icon: <ChatBubbleOutlineIcon sx={{ fontSize: '2rem', color: 'var(--accent-primary)' }} />,
            circleBg: '#f0e6fa',
            title: getField(dictionary, 'review-managment', language),
            description: getField(dictionary, 'we-help-generate', language)
        },
        {
            icon: <BarChartIcon sx={{ fontSize: '2rem', color: 'var(--accent-secondary)' }} />,
            circleBg: '#fffbe6',
            title: getField(dictionary, 'performance-tracking', language),
            description: getField(dictionary, 'monthly-reports', language)
        },
        {
            icon: <MenuBookIcon sx={{ fontSize: '2rem', color: 'var(--accent-primary)' }} />,
            circleBg: '#f0e6fa',
            title: getField(dictionary, 'menu-optimization', language),
            description: getField(dictionary, 'we-optimize-online-menu', language)
        },
        {
            icon: <LinkIcon sx={{ fontSize: '2rem', color: 'var(--accent-secondary)' }} />,
            circleBg: '#fffbe6',
            title: getField(dictionary, 'citation-building', language),
            description: getField(dictionary, 'we-create', language)
        },
    ];

    const withoutSeoPoints = [
        getField(dictionary, 'invisible-local-search', language), ,
        getField(dictionary, 'competitors-capture', language), ,
        getField(dictionary, 'inaccurate-missing-buisness', language), ,
        getField(dictionary, 'unmanaged-negative-reviews', language), ,
        getField(dictionary, 'reliance-on-expensive', language), ,
    ];

    const withCloudixSeoPoints = [
        getField(dictionary, 'top-rankings', language), ,
        getField(dictionary, 'prominet-placement', language), ,
        getField(dictionary, 'accurate-optimized-buisness', language), ,
        getField(dictionary, 'positive-review-management', language), ,
        getField(dictionary, 'direct-customer', language), ,
    ];
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const handleToggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };
    const faqsQrOrdering = [
        {
            question: getField(dictionary, 'question5', language),
            answer:
                getField(dictionary, 'response5', language)
        },
        {
            question: getField(dictionary, 'question1', language),
            answer:
                getField(dictionary, 'response1', language)
        },
        {
            question: getField(dictionary, 'question2', language),
            answer:
                getField(dictionary, 'response2', language)
        },
        {
            question: getField(dictionary, 'question3', language),
            answer:
                getField(dictionary, 'response3', language)
        },
        {
            question: getField(dictionary, 'question4', language),
            answer:
                getField(dictionary, 'response4', language)
        },

    ];
    const seoResults = [
        { name: getField(dictionary, 'your-rest-name', language), rating: 5, reviews: 120 },
        { name: getField(dictionary, 'competitor-resto', language), rating: 4, reviews: 85 },
        { name: getField(dictionary, 'competitor-resto', language), rating: 3, reviews: 42 },
    ];


    return (
        <>
            <div style={{ width: isMobile ? "95%" : "85%", margin: "auto", display: "flex", flexDirection: "column", alignItems: "center" }}>

                <Grid container spacing={!isMobile && isArabic ? 2 : 4} alignItems="center" flexDirection={'row'} padding={isMobile ? '0px' : '25px'} marginTop={'5rem'}>
                    {/* Left Side */}
                    <Grid item xs={12} sm={12} md={6} style={{ order: isArabic ? 2 : 1 }}>
                        <Paper elevation={3} className="paper-style" sx={{ textAlign: isArabic ? 'right' : 'left', padding: '2rem' }}>
                            <Typography gutterBottom component="h1" variant="h1" width={isMobile ? '100%' : '80%'} fontWeight="bold" fontFamily="Century Gothic" fontSize={isMobile ? '1.5rem' : '2rem'}>
                                {getField(dictionary, 'restaurant-seo', language)}
                            </Typography>

                            <Typography paragraph component="h2" variant="h2" width={isMobile ? '100%' : '80%'} fontFamily="Century Gothic" fontSize={'19px'} sx={{ color: "var(--text-primary)" }}>
                                {getField(dictionary, 'boost-seo', language)}
                            </Typography>

                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: 'var(--accent-primary)',
                                    marginTop: isMobile ? '2rem' : '3rem',
                                    transition: 'background-color 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: 'var(--accent-secondary)',
                                    },
                                }}
                                onClick={() => scrollToSection(contactusRef)}
                            >

                                {getField(dictionary, 'request-demo', language)}
                            </Button>


                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6} style={{ order: !isMobile && isArabic ? 1 : 2 }}>
                        <Box mt={4} p={2} sx={{ backgroundColor: 'var(--section-bg-alt)', borderRadius: "8px" }}>
                            <Box sx={{ backgroundColor: "var(--accent-primary)", padding: "10px" }}>
                                <Typography variant="subtitle1" fontWeight="bold" color="white">
                                    {getField(dictionary, 'search-results', language)}
                                </Typography>
                            </Box>
                            <Grid container direction="column" mt={2}>
                                {seoResults.map((item, index) => (
                                    <Grid item container alignItems="center" mb={1} key={index}>
                                        <Grid item xs={1}>
                                            <Typography variant="h6" fontWeight="bold" color="var(--accent-primary)">{index + 1}</Typography>
                                        </Grid>
                                        <Grid item xs={11}>
                                            <Box display="flex" flexDirection="column">
                                                <Box display="flex" alignItems="baseline">
                                                    <Typography variant="subtitle1" fontWeight={item.rating === 5 ? 'bold' : 'normal'} sx={{ color: item.rating === 5 ? 'var(--accent-primary)' : 'var(--text-primary)' }}>
                                                        {item.name}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center">
                                                    <Typography mr={1}>
                                                        {'⭐'.repeat(item.rating)}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "var(--text-secondary)" }}>
                                                        ({item.reviews}   {getField(dictionary, 'reviews', language)})   {getField(dictionary, 'italian-resto', language)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </div>




            <Box
                sx={{
                    backgroundColor: 'var(--section-bg-alt)',
                    padding: isMobile ? '3rem 1rem' : '5rem 2rem',
                    textAlign: 'center',
                    marginTop: '5rem',
                }}
            >
                <Typography
                    variant={isMobile ? "h6" : "h4"}
                    fontWeight="bold"
                    fontFamily="Century Gothic"
                    gutterBottom
                >
                    {getField(dictionary, 'why-restaurant-seo-matters', language)}
                </Typography>
                <Typography
                    variant="body1"
                    fontSize={isMobile ? '1rem' : '1.1rem'}
                    fontFamily="Century Gothic"
                    sx={{ color: "var(--text-primary)" }}
                    mb={4}
                >
                    {getField(dictionary, 'in-today-digital', language)}
                </Typography>

                <Grid container spacing={3} justifyContent="center" >
                    {[
                        {
                            percent: `${getField(dictionary, '81', language)}%`,
                            text: getField(dictionary, 'of-diners', language),
                        },
                        {
                            percent: `${getField(dictionary, '92', language)}%`,
                            text: getField(dictionary, 'of-consumers', language),
                        },
                        {
                            percent: `${getField(dictionary, '70', language)}%`,
                            text: getField(dictionary, 'of-consumers-never', language),
                        },
                    ].map((item, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index} marginTop={isMobile ? '1rem' : '2rem'}>
                            <Paper
                                elevation={3}
                                sx={{
                                    padding: '1rem',
                                    borderRadius: '12px',
                                    height: '100%',
                                    backgroundColor: 'var(--section-bg)'


                                }}
                            >
                                <Typography
                                    variant={isMobile ? "h6" : "h5"}
                                    color="var(--accent-primary)"
                                    fontWeight="bold"
                                    fontFamily="Century Gothic"
                                    mb={1}
                                >
                                    {item.percent}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "var(--text-primary)" }}
                                    fontFamily="Century Gothic"
                                >
                                    {item.text}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <div style={{ width: isMobile ? "95%" : "85%", margin: "auto", display: "flex", flexDirection: "column", alignItems: "center" }}>

                <Box sx={{
                    textAlign: 'center',
                    marginTop: '5rem',
                    marginBottom: '3rem',
                    padding: isMobile ? '0 1rem' : '0 0',
                }}>
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        component="h1"
                        fontWeight="bold"
                        fontFamily="Century Gothic"
                        gutterBottom
                        fontSize={isMobile ? "1rem" : "2rem"}
                    >
                        {getField(dictionary, 'ai-seo-restaurant', language)}
                    </Typography>
                    <Typography
                        variant="h6"
                        component="h2"
                        fontFamily="Century Gothic"
                        sx={{ color: "var(--text-primary)" }}
                        fontSize={isMobile ? "1rem" : "1rem"}
                    >
                        {getField(dictionary, 'our-advanced-ai', language)}
                    </Typography>
                </Box>


                <Grid container spacing={isMobile ? 2 : 4} alignItems="stretch" padding={isMobile ? "0px" : "25px"}>

                    <Grid item xs={12} md={6} sx={{ order: isArabic ? 2 : 1 }}>
                        <Paper
                            elevation={3}
                            sx={{
                                backgroundColor: 'var(--accent-primary)',
                                color: 'white',
                                padding: isMobile ? '1.5rem' : '2rem',
                                borderRadius: '12px',
                                textAlign: isArabic ? 'right' : 'left',
                                height: '60%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box mb={2}>
                                <Box display="flex" alignItems="center" mb={1} flexDirection={isArabic ? 'row-reverse' : 'row'}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#BE7DF9',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <img
                                            src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/robot.png`}
                                            style={{ width: '32px', height: '32px' }}
                                            alt="Robot Icon"
                                        />
                                    </Box>
                                    <Typography variant="h5" component="h3" fontWeight="bold" fontFamily="Century Gothic" paddingLeft={isArabic ? '0' : '10px'} paddingRight={isArabic ? '10px' : 0} fontSize={isMobile ? '1rem' : '2rem'}>
                                        {getField(dictionary, 'cloudix-ai-engine', language)}
                                    </Typography>
                                </Box>
                                <Typography variant="subtitle1" fontFamily="Century Gothic">
                                    {getField(dictionary, 'intellignet-seo-optimization', language)}
                                </Typography>
                            </Box>

                            <List disablePadding sx={{ width: '100%', mb: 2 }}>
                                {cloudixFeatures.map((text, index) => (
                                    <ListItem key={index} disablePadding sx={{ mb: 1, alignItems: isArabic ? 'center' : 'flex-start', flexDirection: isArabic ? 'row-reverse' : 'row' }}>
                                        <ListItemIcon sx={{
                                            minWidth: '30px',
                                            ...(isArabic && { mr: 0, ml: 1 })
                                        }}>
                                            <CheckCircleOutlineIcon sx={{ color: 'var(--accent-secondary)', fontSize: '1.2rem' }} />
                                        </ListItemIcon>
                                        <ListItemText primary={
                                            <Typography variant="body1" fontFamily="Century Gothic" fontSize={isMobile ? "0.9rem" : "1rem"} color="white">
                                                {text}
                                            </Typography>
                                        } sx={{ textAlign: isArabic ? 'right' : 'left' }} />
                                    </ListItem>
                                ))}
                            </List>

                            <Box
                                sx={{
                                    backgroundColor: '#BF7DF9',
                                    borderRadius: '8px',
                                    padding: '1rem',
                                    textAlign: isArabic ? 'right' : 'left',
                                }}
                            >
                                <Typography variant="caption" fontFamily="Century Gothic" fontSize={isMobile ? "0.7rem" : "0.8rem"} color="white">
                                    {getField(dictionary, 'our-ai-analyzes', language)}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>


                    <Grid item xs={12} md={6} sx={{ order: isArabic ? 1 : 2 }}>
                        <Box sx={{
                            padding: isMobile ? '1.5rem' : '1rem',
                            textAlign: isArabic ? 'right' : 'left',
                            height: '100%',
                        }}>
                            <Typography variant={isMobile ? 'h6' : 'h5'} component="h5" fontWeight="bold" fontFamily="Century Gothic" mb={isMobile ? 2 : 3} sx={{ color: "var(--text-primary)" }}>
                                {getField(dictionary, 'how-our-ai', language)}
                            </Typography>

                            <List disablePadding>
                                {aiTransformFeatures.map((feature, index) => (
                                    <ListItem key={index} disablePadding sx={{ alignItems: 'flex-start', mb: isMobile ? 2 : 3, flexDirection: isArabic ? 'row-reverse' : 'row', }}>
                                        <ListItemIcon sx={{ minWidth: '30px', mt: '4px' }}>
                                            <Box
                                                sx={{
                                                    backgroundColor: mode === 'dark' ? 'rgba(148, 99, 164, 0.2)' : 'rgba(129, 48, 203, 0.2)',
                                                    borderRadius: '50%',
                                                    padding: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: isArabic ? 'flex-end' : 'flex-start',
                                                    width: '25px',
                                                    height: '25px',

                                                }}
                                            >
                                                {feature.icon}
                                            </Box>
                                        </ListItemIcon>

                                        <ListItemText
                                            primary={
                                                <Typography variant="h6" component="h4" fontWeight="bold" fontFamily="Century Gothic" fontSize={isMobile ? "1rem" : "1.1rem"} paddingLeft={isArabic ? '0px' : '10px'} paddingRight={isArabic ? '10px' : 0} sx={{ color: "var(--text-primary)" }}>
                                                    {feature.title}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography variant="body2" fontFamily="Century Gothic" sx={{ color: "var(--text-primary)" }} fontSize={isMobile ? "0.9rem" : "1rem"} paddingLeft={isArabic ? '0px' : '10px'} paddingRight={isArabic ? '10px' : 0}>
                                                    {feature.description}
                                                </Typography>
                                            }
                                            sx={{ textAlign: isArabic ? 'right' : 'left' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Grid>
                </Grid>






            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
                <Box
                    sx={{
                        backgroundColor: 'var(--section-bg)',
                        padding: isMobile ? '3rem 1rem' : '5rem 2rem',
                        textAlign: 'center',
                        marginTop: isMobile ? '' : '5rem',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        borderRadius: '12px',
                        width: isMobile ? '90%' : "70%"
                    }}
                >
                    <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        fontWeight="bold"
                        fontFamily="Century Gothic"
                        gutterBottom
                    >
                        {getField(dictionary, 'ai-powered-results', language)}
                    </Typography>


                    <Grid container spacing={3} justifyContent="center" >
                        {[
                            {
                                percent: getField(dictionary, '3.4x', language),
                                text: getField(dictionary, 'more-accurate-keyword', language),
                            },
                            {
                                percent: `${getField(dictionary, '67', language)}%`,
                                text: getField(dictionary, 'faster-ranking', language),
                            },
                            {
                                percent: getField(dictionary, '24/7', language),
                                text: getField(dictionary, 'continious-optimization', language),
                            },
                        ].map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index} marginTop={isMobile ? '1.5rem' : '1rem'}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        padding: '1rem',
                                        borderRadius: '12px',
                                        height: '100%',
                                        backgroundColor: 'var(--section-bg-alt)'
                                    }}
                                >
                                    <Typography
                                        variant={isMobile ? 'h6' : 'h5'}
                                        color="var(--accent-primary)"
                                        fontWeight="bold"
                                        fontFamily="Century Gothic"
                                        mb={1}
                                    >
                                        {item.percent}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "var(--text-primary)" }}
                                        fontFamily="Century Gothic"
                                    >
                                        {item.text}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
                <Box
                    sx={{
                        backgroundColor: 'transparent',
                        padding: isMobile ? '3rem 1rem' : '5rem 2rem',
                        textAlign: 'center',
                        marginTop: isMobile ? '0' : '5rem',
                        width: isMobile ? '90%' : "80%"
                    }}
                >
                    <Typography
                        variant={isMobile ? 'h6' : 'h4'}
                        fontWeight="bold"
                        fontFamily="Century Gothic"
                        gutterBottom
                        mb={isMobile ? 2 : 4}
                    >
                        {getField(dictionary, 'how-diners', language)}
                    </Typography>

                    <Grid container spacing={isMobile ? 3 : 6} alignItems="flex-start" justifyContent="center">

                        <Grid item xs={12} md={6}>

                            <Box sx={{ textAlign: isArabic ? 'right' : 'left', mb: isMobile ? 4 : 6 }}>
                                <Typography
                                    variant={isMobile ? 'h6' : 'h5'}
                                    fontWeight="bold"
                                    fontFamily="Century Gothic"
                                    gutterBottom
                                    fontSize={isMobile ? "0.9rem" : "1.3rem"}

                                >
                                    {getField(dictionary, 'google-search-maps', language)}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontFamily="Century Gothic"
                                    sx={{ color: "var(--text-primary)" }}
                                    mb={3}
                                >
                                    {getField(dictionary, 'when-hungry-customers', language)}
                                </Typography>
                                <List disablePadding>
                                    {googleSearchStats.map((item, index) => (
                                        <ListItem key={index} disablePadding sx={{ mb: 1.5, alignItems: 'flex-start', flexDirection: isArabic ? 'row-reverse' : 'row' }}>
                                            <ListItemIcon sx={{ minWidth: '25px', paddingLeft: isArabic ? 0 : '10px', paddingRight: isArabic ? '10px' : '0' }}  >
                                                <CheckCircleOutlineIcon sx={{ color: 'var(--accent-primary)', fontSize: '2rem' }} />
                                            </ListItemIcon>
                                            <ListItemText primary={
                                                <Typography variant="body1" fontFamily="Century Gothic" fontSize={isMobile ? "0.9rem" : "1rem"} sx={{ color: "var(--text-primary)" }}>
                                                    <Typography component="span" fontWeight="bold" color="var(--accent-primary)" >
                                                        {item.percent}
                                                    </Typography>
                                                    {` ${item.text}`}
                                                </Typography>
                                            } sx={{ textAlign: isArabic ? 'right' : 'left' }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>


                            <Paper
                                elevation={1}
                                sx={{
                                    backgroundColor: 'var(--section-bg)',
                                    padding: '2rem',
                                    borderRadius: '12px',
                                    textAlign: isArabic ? 'right' : 'left',
                                    border: '1px solid var(--border-color)'
                                }}
                            >
                                <Box display="flex" alignItems="center" mb={2} flexDirection={isArabic ? 'row-reverse' : 'row'}>
                                    <Box
                                        sx={{
                                            backgroundColor: 'var(--section-bg-hover)',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <img
                                            src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/maps.png`}
                                            style={{ width: '25px', height: '32px' }}
                                            alt="Robot Icon"
                                        />
                                    </Box>

                                    <Typography variant="h6" component="h4" fontWeight="bold" fontFamily="Century Gothic" color="var(--accent-primary)" paddingLeft={isArabic ? 0 : '10px'}
                                        paddingRight={isArabic ? '10px' : 0}>
                                        {getField(dictionary, 'google-maps-search', language)}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" fontFamily="Century Gothic" sx={{ color: "var(--text-primary)" }} mb={2}>
                                    {getField(dictionary, 'how-customers-find', language)}
                                </Typography>
                                <List disablePadding>
                                    {googleMapsExamples.map((item, index) => (
                                        <Box
                                            sx={{
                                                backgroundColor: '#BE7DF936',
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: "7px", marginTop: "1rem"
                                            }}
                                        >
                                            <ListItem key={index} disablePadding sx={{ mb: 1.5, alignItems: 'flex-start', flexDirection: isArabic ? 'row-reverse' : 'row', }} >
                                                <ListItemIcon sx={{ minWidth: '40px' }} >
                                                    <Box
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: '50%',
                                                            backgroundColor: 'var(--accent-primary)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: '0.8rem',
                                                        }}
                                                    >
                                                        {item.rank}
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primary={
                                                    <Typography variant="body1" fontFamily="Century Gothic" fontWeight="bold" fontSize={isMobile ? "0.9rem" : "1rem"} textAlign={isArabic ? 'right' : 'left'} paddingLeft={isArabic ? 0 : '10px'}
                                                        paddingRight={isArabic ? '10px' : 0} sx={{ color: "var(--text-primary)" }}>
                                                        {item.name}
                                                    </Typography>
                                                } secondary={
                                                    <Typography variant="body2" fontFamily="Century Gothic" fontSize={isMobile ? "0.8rem" : "0.9rem"} sx={{ color: "var(--text-primary)" }}>
                                                        {'⭐'.repeat(item.stars)} ({item.reviews}+ {getField(dictionary, 'reviews', language)}) {item.type}
                                                    </Typography>
                                                } sx={{ textAlign: isArabic ? 'right' : 'left' }} />
                                            </ListItem>
                                        </Box>

                                    ))}
                                </List>
                                <Typography variant="body2" fontFamily="Century Gothic" sx={{ color: "var(--text-primary)" }} mt={3} fontSize={isMobile ? "0.9rem" : "1rem"}>
                                    {getField(dictionary, 'restaurant-with-optimized', language)} <Typography component="span" fontWeight="bold" sx={{ color: "var(--text-primary)" }}>5-7x {getField(dictionary, 'more-visibility', language)}.</Typography>
                                </Typography>
                            </Paper>
                        </Grid>


                        <Grid item xs={12} md={6}>

                            <Paper
                                elevation={3}
                                sx={{
                                    backgroundColor: 'var(--section-bg)',
                                    padding: '2rem',
                                    borderRadius: '12px',
                                    textAlign: isArabic ? 'right' : 'left',
                                    mb: isMobile ? 4 : 6,
                                    border: '1px solid var(--border-color)'
                                }}
                            >
                                <Box display="flex" alignItems="center" mb={2} flexDirection={isArabic ? 'row-reverse' : 'row'}>

                                    <img
                                        src={`${process.env.NEXT_PUBLIC_IMAGE_PATH}/assests/images/earth.png`}
                                        style={{ width: '32px', height: '32px' }}
                                        alt="Robot Icon"
                                    />


                                    <Typography variant="h6" component="h4" fontWeight="bold" fontFamily="Century Gothic" color="var(--accent-primary)" paddingLeft={isArabic ? 0 : '10px'}
                                        paddingRight={isArabic ? '10px' : 0}>
                                        {getField(dictionary, 'google-search', language)}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant={isMobile ? 'h6' : 'h5'}
                                    fontWeight="bold"
                                    fontFamily="Century Gothic"
                                    gutterBottom
                                    mb={2}
                                    fontSize={isMobile ? "0.9rem" : "1.3rem"}
                                    sx={{ color: "var(--text-primary)" }}
                                >
                                    {getField(dictionary, 'top-resto', language)}
                                </Typography>
                                <List disablePadding>
                                    {topSearchQueries.map((query, index) => (
                                        <ListItem key={index} disablePadding sx={{
                                            mb: 1, alignItems: 'flex-start',
                                            flexDirection: isArabic ? 'row-reverse' : 'row'
                                        }}>
                                            <ListItemText primary={
                                                <Typography variant="body1" fontFamily="Century Gothic" fontSize={isMobile ? "0.9rem" : "1rem"} sx={{ color: "var(--text-primary)" }}>
                                                    {query}
                                                </Typography>
                                            } sx={{ textAlign: isArabic ? 'right' : 'left' }} />
                                        </ListItem>
                                    ))}
                                </List>
                                <Typography variant="body2" fontFamily="Century Gothic" sx={{ color: "var(--text-primary)" }} mt={3} fontSize={isMobile ? "0.9rem" : "1rem"}>
                                    {getField(dictionary, 'without-proper-seo', language)}
                                </Typography>
                            </Paper>


                            <Box sx={{ textAlign: isArabic ? 'right' : 'left' }}>
                                <Typography
                                    variant={isMobile ? 'h6' : 'h5'}
                                    fontWeight="bold"
                                    fontFamily="Century Gothic"
                                    gutterBottom
                                    fontSize={isMobile ? "0.9rem" : "1rem"}
                                >
                                    {getField(dictionary, 'mobile-search', language)}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontFamily="Century Gothic"
                                    sx={{ color: "var(--text-primary)" }}
                                    mb={3}
                                >
                                    {getField(dictionary, 'with-consumers', language)}
                                </Typography>
                                <List disablePadding>
                                    {mobileSearchStats.map((item, index) => (
                                        <ListItem key={index} disablePadding sx={{
                                            mb: 1.5, alignItems: 'flex-start',
                                            flexDirection: isArabic ? 'row-reverse' : 'row'
                                        }}>
                                            <ListItemIcon sx={{ minWidth: '30px', ...(isArabic && { mr: 0, ml: 1 }) }}>
                                                <CheckCircleOutlineIcon sx={{ color: 'var(--accent-secondary)', fontSize: '2.2rem' }} />
                                            </ListItemIcon>
                                            <ListItemText primary={
                                                <Typography variant="body1" fontFamily="Century Gothic" fontSize={isMobile ? "0.9rem" : "1rem"} sx={{ color: "var(--text-primary)" }}>
                                                    <Typography component="span" fontWeight="bold" color="var(--accent-secondary)">
                                                        {item.percent}
                                                    </Typography>
                                                    {` ${item.text}`}
                                                </Typography>
                                            } sx={{ textAlign: isArabic ? 'right' : 'left' }} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </div >
            <Box
                sx={{
                    backgroundColor: 'transparent',
                    padding: isMobile ? '3rem 1rem' : '5rem 2rem',
                    textAlign: 'center',

                }}
            >
                <Typography
                    variant={isMobile ? 'h6' : 'h4'}
                    fontWeight="bold"
                    fontFamily="Century Gothic"
                    gutterBottom
                    mb={isMobile ? 2 : 4}
                    sx={{ color: "var(--text-primary)" }}
                >
                    {getField(dictionary, 'how-cloudix-rest', language)}
                </Typography>
                <Typography
                    variant="body1"
                    fontSize="1.1rem"
                    fontFamily="Century Gothic"
                    sx={{ color: "var(--text-primary)" }}
                    mb={isMobile ? 4 : 6}
                >
                    {getField(dictionary, 'our-comprehensive', language)}
                </Typography>

                <Grid container spacing={isMobile ? 4 : 6} justifyContent="center" alignItems="flex-start">
                    {howItWorksSteps.map((step, index) => (
                        <Grid item xs={12} sm={4} md={4} key={index}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                padding: '1rem',
                            }}>

                                <Box
                                    sx={{
                                        width: isMobile ? 80 : 100,
                                        height: isMobile ? 80 : 100,
                                        borderRadius: '50%',
                                        backgroundColor: step.circleBg,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: isMobile ? 2 : 3,
                                    }}
                                >
                                    <img src={step.icon} width={'50px'} height={'50px'} />
                                </Box>
                                <Typography
                                    variant={isMobile ? 'h6' : 'h4'}
                                    fontFamily="Century Gothic"
                                    sx={{ color: "var(--text-primary)" }}
                                    fontWeight={'bold'}
                                    fontSize={isMobile ? "0.9rem" : "1rem"}
                                >
                                    {step.title}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontFamily="Century Gothic"
                                    sx={{ color: "var(--text-primary)" }}
                                    fontSize={isMobile ? "0.9rem" : "1rem"}
                                    marginTop={'1rem'}
                                >
                                    {step.description}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
            <div style={{ display: "flex", justifyContent: "center" }}>
                <Box
                    sx={{
                        padding: isMobile ? '3rem 1rem' : '5rem 2rem',
                        textAlign: 'center',
                        width: isMobile ? "90%" : "80%",

                    }}
                >
                    <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        fontWeight="bold"
                        fontFamily="Century Gothic"
                        gutterBottom
                        mb={isMobile ? 2 : 4}
                        sx={{ color: "var(--text-primary)" }}
                    >
                        {getField(dictionary, 'rest-seo-features', language)}
                    </Typography>
                    <Typography
                        variant="body1"
                        fontSize="1.1rem"
                        fontFamily="Century Gothic"
                        sx={{ color: "var(--text-primary)" }}
                        mb={isMobile ? 4 : 6}
                    >
                        {getField(dictionary, 'everything-you-need', language)}
                    </Typography>

                    <Grid
                        container
                        spacing={isMobile ? 3 : 4}
                        justifyContent="center"
                        alignItems="stretch"
                    >
                        {seoFeatures.map((feature, index) => (
                            <Grid
                                item
                                xs={12}
                                md={6}
                                key={index}
                                sx={{ display: 'flex' }}
                            >
                                <Paper
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: isArabic ? 'row-reverse' : 'row',
                                        alignItems: 'flex-start',
                                        backgroundColor: 'var(--section-bg)',
                                        padding: isMobile ? '1.5rem' : '2rem',
                                        borderRadius: '12px',
                                        textAlign: isArabic ? 'right' : 'left',
                                        boxShadow: `
            0px 3px 1px -2px rgba(0,0,0,0.1),
            0px 2px 2px 0px rgba(0,0,0,0.07),
            0px 1px 5px 0px rgba(0,0,0,0.05)
          `,
                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: `
              0px 6px 10px rgba(0, 0, 0, 0.1),
              0px 3px 6px rgba(0, 0, 0, 0.07)
            `,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: isMobile ? 50 : 60,
                                            height: isMobile ? 50 : 60,
                                            borderRadius: '50%',
                                            backgroundColor: feature.circleBg,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            mr: isArabic ? 0 : 2,
                                            ml: isArabic ? 2 : 0,
                                        }}
                                    >
                                        {feature.icon}
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            component="h3"
                                            fontWeight="bold"
                                            fontFamily="Century Gothic"
                                            gutterBottom
                                            fontSize={isMobile ? "1rem" : "1.1rem"}
                                            sx={{ color: "var(--text-primary)" }}
                                        >
                                            {feature.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontFamily="Century Gothic"
                                            sx={{ color: "var(--text-primary)" }}
                                            fontSize={isMobile ? "0.9rem" : "1rem"}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>



                </Box>
            </div>
            <Box
                sx={{
                    backgroundColor: 'var(--section-bg-alt)',
                    padding: isMobile ? '3rem 1rem' : '5rem 2rem',
                    textAlign: 'center',

                }}
            >
                <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    fontWeight="bold"
                    fontFamily="Century Gothic"
                    gutterBottom
                >
                    {getField(dictionary, 'the-impact-of-seo', language)}
                </Typography>
                <Typography
                    variant="body1"
                    fontSize="1.1rem"
                    fontFamily="Century Gothic"
                    sx={{ color: "var(--text-primary)" }}
                    mb={4}
                >
                    {getField(dictionary, 'real-world-results', language)}
                </Typography>

                <Grid container spacing={3} justifyContent="center" >
                    {[
                        {
                            percent: getField(dictionary, '45%', language),
                            title: getField(dictionary, 'more-website-traffic', language),
                            text: getField(dictionary, 'increase-potential', language),
                        },
                        {
                            percent: getField(dictionary, '83%', language),
                            title: getField(dictionary, 'more-reservations', language),
                            text: getField(dictionary, 'growth-online', language),
                        },
                        {
                            percent: getField(dictionary, '52%', language),
                            title: getField(dictionary, 'more-map-views', language),
                            text: getField(dictionary, 'increase-google', language)
                        },
                    ].map((item, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index} marginTop={isMobile ? '1rem' : ''}>
                            <Paper
                                elevation={3}
                                sx={{
                                    padding: '1rem',
                                    borderRadius: '12px',
                                    height: '100%',
                                    backgroundColor: 'var(--section-bg)'


                                }}
                            >
                                <Typography
                                    variant={isMobile ? 'h6' : 'h5'}
                                    color="var(--accent-primary)"
                                    fontWeight="bold"
                                    fontFamily="Century Gothic"
                                    mb={1}
                                >
                                    {item.percent}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    fontWeight={'bold'}
                                    fontFamily="Century Gothic"
                                    marginBottom={'1rem'}
                                    sx={{ color: "var(--text-primary)" }}
                                >
                                    {item.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "var(--text-primary)" }}
                                    fontFamily="Century Gothic"
                                >
                                    {item.text}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
                <div style={{
                    display: "flex",
                    justifyContent: "center"
                }}>
                    <Paper
                        elevation={3}
                        sx={{
                            padding: '1rem',
                            borderRadius: '12px',
                            height: '100%',
                            marginTop: '5rem',
                            width: isMobile ? "100%" : '80%',
                            backgroundColor: 'var(--section-bg)'


                        }}
                    >

                        <Box
                            sx={{
                                padding: isMobile ? '1rem 1rem' : '0.5rem 0.5rem',
                                textAlign: 'center',

                                borderRadius: '12px',



                            }}
                        >
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                fontFamily="Century Gothic"
                                gutterBottom
                                mb={isMobile ? 3 : 5}
                                sx={{ color: "var(--text-primary)" }}
                            >
                                {getField(dictionary, 'why-rest-without-seo', language)}
                            </Typography>

                            <Grid container spacing={isMobile ? 3 : 6} justifyContent="center">

                                {/* Without SEO */}
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ textAlign: isArabic ? 'right' : 'left', px: isMobile ? 1 : 2 }}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            mb={isMobile ? 2 : 3}
                                            flexDirection={isArabic ? 'row-reverse' : 'row'}
                                        >
                                            <ClearIcon
                                                sx={{
                                                    color: '#FF0000',
                                                    fontSize: '1.5rem',
                                                    mr: isArabic ? 0 : 1,
                                                    ml: isArabic ? 1 : 0,
                                                }}
                                            />
                                            <Typography
                                                variant="h6"
                                                component="h3"
                                                fontWeight="bold"
                                                fontFamily="Century Gothic"
                                                color="#FF0000"
                                                fontSize={isMobile ? "1.1rem" : "1.2rem"}
                                            >
                                                {getField(dictionary, 'without-seo', language)}
                                            </Typography>
                                        </Box>

                                        <List disablePadding>
                                            {withoutSeoPoints.map((text, index) => (
                                                <ListItem
                                                    key={index}
                                                    disablePadding
                                                    sx={{
                                                        alignItems: isMobile ? 'flex-start' : 'center',
                                                        flexDirection: isArabic ? 'row-reverse' : 'row',
                                                        marginLeft: isArabic ? 0 : "2rem",
                                                        marginRight: isArabic ? "2rem" : 0,
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            minWidth: '30px',
                                                            display: 'flex',
                                                            justifyContent: isArabic ? 'flex-end' : 'flex-start',
                                                        }}
                                                    >
                                                        <ClearIcon sx={{ color: '#FF0000', fontSize: '1rem' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="body1"
                                                                fontFamily="Century Gothic"
                                                                fontSize={isMobile ? "0.9rem" : "1rem"}
                                                                sx={{ color: "var(--text-primary)" }}
                                                            >
                                                                {text}
                                                            </Typography>
                                                        }
                                                        sx={{ textAlign: isArabic ? 'right' : 'left' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                </Grid>

                                {/* With Cloudix SEO */}
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ textAlign: isArabic ? 'right' : 'left', px: isMobile ? 1 : 2 }}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            mb={isMobile ? 2 : 3}
                                            flexDirection={isArabic ? 'row-reverse' : 'row'}
                                        >
                                            <CheckCircleOutlineIcon
                                                sx={{
                                                    color: '#4CAF50',
                                                    fontSize: '1.5rem',
                                                    mr: isArabic ? 0 : 1,
                                                    ml: isArabic ? 1 : 0,
                                                }}
                                            />
                                            <Typography
                                                variant="h6"
                                                component="h3"
                                                fontWeight="bold"
                                                fontFamily="Century Gothic"
                                                color="#4CAF50"
                                                fontSize={isMobile ? "1.1rem" : "1.2rem"}
                                            >
                                                {getField(dictionary, 'with-cloudix-seo', language)}
                                            </Typography>
                                        </Box>

                                        <List disablePadding>
                                            {withCloudixSeoPoints.map((text, index) => (
                                                <ListItem
                                                    key={index}
                                                    disablePadding
                                                    sx={{
                                                        alignItems: isMobile ? 'flex-start' : 'center',
                                                        flexDirection: isArabic ? 'row-reverse' : 'row',
                                                        marginLeft: isArabic ? 0 : "2rem",
                                                        marginRight: isArabic ? "2rem" : 0,
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{
                                                            minWidth: '30px',
                                                            display: 'flex',
                                                            justifyContent: isArabic ? 'flex-end' : 'flex-start',
                                                        }}
                                                    >
                                                        <CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: '1rem' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="body1"
                                                                fontFamily="Century Gothic"
                                                                fontSize={isMobile ? "0.9rem" : "1rem"}
                                                                sx={{ color: "var(--text-primary)" }}
                                                            >
                                                                {text}
                                                            </Typography>
                                                        }
                                                        sx={{ textAlign: isArabic ? 'right' : 'left' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                </Grid>
                            </Grid>

                        </Box>
                    </Paper>
                </div>


            </Box>
            <div>
                <Box style={{ width: "100%", display: "flex", justifyContent: isMobile ? "space-between" : "center", marginTop: "6rem" }}>
                    <div className='container' style={{ padding: "1rem" }}>
                        <Box className="box-container" sx={{ width: isMobile ? "95%" : "900px" }}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <div style={{ display: "flex", alignItems: "center", flexDirection: "column", lineHeight: 1 }}>
                                    <p className='faq-title'>{getField(dictionary, 'faq', language)}  </p>
                                    <p className="fap-under-title" style={{ fontSize: isMobile ? "15px" : "19px" }}> {getField(dictionary, 'what-are-you-looking-for', language)} </p>
                                </div>
                                <div><SearchIcon sx={{ fontSize: "70px", color: "var(--accent-secondary)" }} /> </div>
                            </div>
                            <Grid container spacing={0} alignItems="center" justifyContent="center" marginTop="1rem">
                                <Grid item xs={12} md={5} justifyContent={isMobile ? "center" : "flex-start"}>
                                    <Box >
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: isMobile ? "center" : "flex-start", marginTop: "1rem" }}>
                                            <div >
                                                {getSvg('arrowDownYellowIcon', { yellow: 'var(--accent-secondary)', darkPurple: 'var(--accent-primary)' }, { width: isMobile ? 70 : 100, height: isMobile ? 70 : 100 }, { transform: isMobile ? "translateX(2rem) " : "translateX(3rem)" })}
                                            </div>
                                            <div style={{ marginLeft: "1rem" }}>
                                                <p style={{ fontSize: "15px", color: "var(--text-primary)", fontFamily: "Century Gothic", margin: 0 }}> {getField(dictionary, 'cant-find-what', language)}</p>
                                                <p style={{ width: "90%", fontSize: isMobile ? "15px" : "17px", fontWeight: "bold", color: "var(--text-primary)", fontFamily: "Century Gothic", margin: 0 }}>
                                                    {getField(dictionary, 'we-would-like', language)}
                                                </p>
                                                <div style={{

                                                    marginTop: isMobile ? "2rem" : "4rem",

                                                }} className='chat-container'>
                                                    <span className='whatsapp-container'>
                                                        {getSvg('chattingIcon', { yellow: 'var(--accent-secondary)', darkPurple: 'var(--accent-primary)' }, { width: 60, height: 60 }, { display: "inline-block" })}
                                                        <span><a href={`https://wa.me/${phoneNumber}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                                            {isArabic ?
                                                                (<><span >!</span>    {getField(dictionary, 'chat-with-us', language)} </>)
                                                                :
                                                                (<>   {getField(dictionary, 'chat-with-us', language)} <span >!</span></>)}
                                                        </a></span>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        {faqsQrOrdering.map((question, index) => (
                                            <>
                                                <Box key={index} className='quest-box' style={{ direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left' }}>
                                                    <div><p className='question' style={{ fontSize: isMobile ? "15px" : "15px" }}>{question.question}</p></div>
                                                    <div>
                                                        <IconButton className='question-icon' onClick={() => handleToggle(index)} aria-label="add or remove button seoService">
                                                            {openIndex === index ? <RemoveIcon /> : <AddIcon />}
                                                        </IconButton>
                                                    </div>
                                                </Box>
                                                {openIndex === index && (
                                                    <Box className='answr-box' style={{ direction: isArabic ? 'rtl' : 'ltr', textAlign: isArabic ? 'right' : 'left' }}>
                                                        <p style={{ fontSize: isMobile ? "14px" : "16px", color: "var(--text-primary)", fontFamily: "Century Gothic", margin: 0 }} dangerouslySetInnerHTML={{ __html: question.answer }} />
                                                    </Box>
                                                )}
                                            </>
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>



                        </Box>
                    </div>
                </Box>

            </div>
        </>
    )
}
